import api from './apiClient';

export interface TicketComment {
  _id: string;
  text: string;
  createdBy: { _id: string; firstName: string; lastName: string; email: string } | string;
  createdAt: string;
}

export type TicketStatus = 'enviado' | 'en_progreso' | 'bloqueado' | 'enviado_revision' | 'cerrado';

export interface SupportTicket {
  _id: string;
  code: string;
  company?: { _id: string; name: string } | string;
  subject: string;
  description: string;
  category: 'problema_plataforma' | 'error_sistema' | 'solicitud_funcionalidad' | 'consulta' | 'otro';
  reportedBy: { _id: string; firstName: string; lastName: string; email: string } | string;
  submittedBy?: { _id: string; firstName: string; lastName: string; email: string } | string;
  assignedTo: Array<{
    user: { _id: string; firstName: string; lastName: string; email: string } | string;
    name: string;
    role?: string;
  }>;
  severity: 'leve' | 'moderado' | 'grave';
  attachments: string[];
  status: TicketStatus;
  reviewedBy?: { _id: string; firstName: string; lastName: string; email: string } | string;
  reviewNotes?: string;
  comments: TicketComment[];
  closedBy?: { _id: string; firstName: string; lastName: string; email: string } | string;
  closedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSupportTicketData {
  subject: string;
  description: string;
  category?: string;
  severity?: string;
  companyId?: string;
  onBehalfOfUserId?: string;
  attachments?: File[];
}

class SupportTicketApi {
  async create(data: CreateSupportTicketData): Promise<SupportTicket> {
    const formData = new FormData();
    formData.append('subject', data.subject);
    formData.append('description', data.description);

    if (data.category) formData.append('category', data.category);
    if (data.severity) formData.append('severity', data.severity);
    if (data.companyId) formData.append('companyId', data.companyId);
    if (data.onBehalfOfUserId) formData.append('onBehalfOfUserId', data.onBehalfOfUserId);

    if (data.attachments) {
      for (const file of data.attachments) {
        formData.append('attachments', file);
      }
    }

    const result = await api.post('/support-tickets', formData);
    return result.data;
  }

  async getAll(params?: {
    page?: number;
    limit?: number;
    status?: string;
    category?: string;
    severity?: string;
    company?: string;
  }): Promise<{ data: SupportTicket[]; pagination: any }> {
    const queryParams: Record<string, any> = {};
    if (params?.page) queryParams.page = String(params.page);
    if (params?.limit) queryParams.limit = String(params.limit);
    if (params?.status) queryParams.status = params.status;
    if (params?.category) queryParams.category = params.category;
    if (params?.severity) queryParams.severity = params.severity;
    if (params?.company) queryParams.company = params.company;

    return await api.get('/support-tickets', queryParams);
  }

  async getById(id: string): Promise<SupportTicket> {
    const result = await api.get(`/support-tickets/${id}`);
    return result.data;
  }

  async getAttachmentUrl(id: string, index: number): Promise<{ url: string; originalName?: string; contentType?: string }> {
    const result = await api.get(`/support-tickets/${id}/attachments/${index}`);
    return result.data;
  }

  async updateStatus(id: string, status: TicketStatus, extra?: { reviewNotes?: string; assignedTo?: any[] }): Promise<SupportTicket> {
    const result = await api.patch(`/support-tickets/${id}/status`, { status, ...extra });
    return result.data;
  }

  async addComment(id: string, text: string): Promise<SupportTicket> {
    const result = await api.post(`/support-tickets/${id}/comments`, { text });
    return result.data;
  }

  async delete(id: string): Promise<void> {
    await api.delete(`/support-tickets/${id}`);
  }

  async getStats(params?: { company?: string }): Promise<{
    enviado: number;
    en_progreso: number;
    bloqueado: number;
    enviado_revision: number;
    cerrado: number;
  }> {
    const queryParams: Record<string, any> = {};
    if (params?.company) queryParams.company = params.company;
    const result = await api.get('/support-tickets/stats', queryParams);
    return result.data;
  }

  async exportPdf(params?: { company?: string; includeInactive?: boolean }): Promise<Blob> {
    const queryParams: Record<string, any> = {};
    if (params?.company) queryParams.company = params.company;
    if (params?.includeInactive !== undefined) queryParams.includeInactive = params.includeInactive ? '1' : '0';

    return api.getBlob('/exports/support-tickets', queryParams);
  }
}

export const supportTicketApi = new SupportTicketApi();
